import React from 'react';
import {useForm} from 'react-hook-form';
import {sendContactForm} from '../../util/contact';
import {useTimeout} from '../../util/hooks/useTimeout';
import * as z from 'zod';
import {zodResolver} from '@hookform/resolvers/zod';
import {Form, FormControl, FormItem, FormLabel, FormField, FormMessage} from '@/components/ui/form';
import {Input} from '@/components/ui/input';
import {Textarea} from '@/components/ui/textarea';
import {toast} from '@/components/ui/use-toast';


const formSchema = z.object({
    name: z.string({
        required_error: "Please enter your name",
    }).min(3, {
        message: "Please enter your name",
    }),
    email: z
        .string({
            required_error: "Please enter your email address",
        })
        .email(),
    phone: z.string().max(30).min(4),
    content: z.string({
        required_error: "Please enter your message",
    }).min(10, {
        message: "Please enter your message",
    }).max(5000, {
        message: "Message is too long",
    }),
});

type ContactFormProps = {
    formId?: string;
}
const ContactForm = (props: ContactFormProps) => {
    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {},
    })

    const [isTimeoutActive, startTimeout] = useTimeout(3000);

    const onSubmit = (values: z.infer<typeof formSchema>) => {
        if (isTimeoutActive) {
            return;
        }
        startTimeout();
        toast({
            title: "Message sent",
            description: "We've received your message and will get back to you shortly.",
        })
        // TODO reset form https://github.com/shadcn/ui/issues/549
        // form.reset({});
        sendContactForm(values).then((response: {
            status?: string;
        }) => {
            if (response?.status !== 'SUCCESS') {
                toast({
                    title: "Something went wrong",
                    description: "Please try again later.",
                })
                return;
            }
            toast({
                title: "Message sent",
                description: "We've received your message and will get back to you shortly.",
            })
            form.reset();
        }).catch(() => {
            toast({
                title: "Something went wrong",
                description: "Please try again later.",
            })
        })
    }

    return (
        <Form {...form}>
            <form id={props.formId} onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Name</FormLabel>
                            <FormControl>
                                <Input {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                                <Input {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Phone</FormLabel>
                            <FormControl>
                                <Input {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="content"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Description</FormLabel>
                            <FormControl>
                                <Textarea placeholder="Your message" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </form>
        </Form>
    )
};


export default ContactForm;
